import React from "react"
import { v4 as uuid } from "uuid"

import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core"
import { useAppDispatch } from "@root/store"
import { useSelector } from "react-redux"
import {
  selectChannel,
  selectChannelAttributes,
  selectChannelTags,
} from "@root/store/selectors"
import {
  addChannelTag,
  emitChannelCustomEvent,
  removeChannelAttribute,
  removeChannelTag,
  setChannelAttribute,
} from "@root/store/thunks"
import {
  Attribute,
  AttributeType,
  CustomEvent,
  isDateAttribute,
  isNumberAttribute,
  isTextAttribute,
  Tag,
} from "@root/store/types"
import RemovableStringValueInput from "@root/components/RemovableStringValueInput"
import { makeAttribute } from "@root/components/Attributes"
import SnackContext from "@root/components/Snackbar"
import EmbeddedContainer from "./EmbeddedContainer"

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    marginBlockStart: theme.spacing(3),
    marginBlockEnd: theme.spacing(1),
  },
  paper: {
    padding: theme.spacing(3),
  },
  actionButton: {
    marginTop: theme.spacing(2),
    alignSelf: "end",
    alignContent: "flex-end",
  },
}))

type TagItem = {
  tag: string
  group: string
  name: string
  description: string
}

const tagItems: TagItem[] = [
  {
    tag: "Basketball",
    group: "device",
    name: "Basketball",
    description: "Get the latest Basketball updates.",
  },
  {
    tag: "Politics",
    group: "device",
    name: "Politics",
    description: "Get the latest Politics updates.",
  },
  {
    tag: "Potent Potables",
    group: "device",
    name: "Potent Potables",
    description: "Get the latest Potent Potables updates.",
  },
]

type AttributeItem = {
  key: string
  name: string
  description?: string
  type: AttributeType
}

const InputTypeForAttribute: { [K in AttributeType]: string } = {
  date: "date",
  text: "text",
  number: "number",
}

const attributeItems: AttributeItem[] = [
  {
    key: "name",
    name: "Name",
    type: "text",
  },
  {
    key: "wp_demo_age",
    name: "Age",
    type: "number",
  },
  {
    key: "birthdate",
    name: "Birthdate",
    type: "date",
  },
]

type EmbeddedSize = {
  key: string
  name: string
  height: string
  width: string
}

const embeddedSizes = [
  { key: "auto-auto", name: "auto x auto", width: "auto", height: "auto" },
  { key: "300px-auto", name: "300px x auto", width: "300px", height: "auto" },
  { key: "auto-300px", name: "auto x 300px", width: "auto", height: "300px" },
  {
    key: "300px-300px",
    name: "300px x 300px",
    width: "300px",
    height: "300px",
  },
  { key: "50%-50%", name: "50% x 50%", width: "50%", height: "50%" },
] as const

const Home: React.FC = () => {
  const dispatch = useAppDispatch()
  const channel = useSelector(selectChannel)
  const channelTags = useSelector(selectChannelTags)
  const channelAttributes = useSelector(selectChannelAttributes)
  const { createSnack } = React.useContext(SnackContext)

  const addTag = React.useCallback(
    (tag: Tag) => {
      dispatch(addChannelTag(tag))
    },
    [dispatch]
  )
  const removeTag = React.useCallback(
    (tag: Tag) => {
      dispatch(removeChannelTag(tag))
    },
    [dispatch]
  )

  const setAttribute = React.useCallback(
    (attribute: Attribute) => {
      dispatch(setChannelAttribute(attribute))
    },
    [dispatch]
  )
  const removeAttribute = React.useCallback(
    (attribute: Attribute) => {
      dispatch(removeChannelAttribute(attribute))
    },
    [dispatch]
  )

  const emitEvent = React.useCallback(
    (event: CustomEvent) => {
      dispatch(emitChannelCustomEvent(event))
    },
    [dispatch]
  )

  const [embeddedId, setEmbeddedId] = React.useState("web_push_demo")
  const [embeddedSize, setEmbeddedSize] = React.useState<EmbeddedSize>(
    embeddedSizes[0]
  )

  const hasChannel = Boolean(channel)

  const classes = useStyles()

  const tags = tagItems.map(({ name, description, tag, group }) => {
    const existing = getTag(group, tag)

    let action: React.ReactElement | null = null
    if (hasChannel && existing) {
      action = (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => removeTag(existing)}
          className={classes.actionButton}
        >
          Remove
        </Button>
      )
    } else {
      action = (
        <Button
          variant="contained"
          color="primary"
          onClick={() => addTag({ tag, group, id: uuid() })}
          className={classes.actionButton}
          disabled={!hasChannel}
        >
          Add
        </Button>
      )
    }

    return (
      <Grid item xs={12} sm={6} md={4} key={`${group}-${tag}`}>
        <Paper className={classes.paper}>
          <Typography variant="h5">{name}</Typography>
          <Typography variant="subtitle2">{description}</Typography>
          {action}
        </Paper>
      </Grid>
    )
  })

  const attributes = attributeItems.map(({ key, name, type, description }) => {
    const attribute = getAttribute(key)

    return (
      <Grid item xs={12} sm={6} md={4} key={key}>
        <Paper className={classes.paper}>
          <RemovableStringValueInput
            id={`attribute-${key}`}
            helpText={
              <>
                {name}{" "}
                <Box component="span" fontFamily="Monospace">
                  ({key})
                </Box>
              </>
            }
            disabled={!hasChannel}
            type={InputTypeForAttribute[type]}
            initialValue={
              attribute ? convertAttributeValueForInput(attribute) : undefined
            }
            onValueSet={(value) => {
              if (value) {
                setAttribute(makeAttribute(type, key, value))
              } else if (attribute) {
                removeAttribute(attribute)
              }
            }}
          />
        </Paper>
      </Grid>
    )
  })

  return (
    <>
      <Typography variant="h6" className={classes.heading}>
        Tags
      </Typography>
      <Grid container spacing={2}>
        {tags}
      </Grid>
      <Typography variant="h6" className={classes.heading}>
        Attributes
      </Typography>
      <Grid container spacing={2}>
        {attributes}
      </Grid>
      <Typography variant="h6" className={classes.heading}>
        Miscellaneous
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper className={classes.paper}>
            <Typography variant="h5">Custom Event</Typography>
            <Typography variant="subtitle2">
              Emit a pre-defined custom event.
            </Typography>
            <Button
              variant="contained"
              className={classes.actionButton}
              disabled={!hasChannel}
              onClick={(ev) => {
                emitEvent({
                  name: "demo_page_button",
                  value: 1,
                  properties: [
                    { key: "x", type: "number", value: ev.nativeEvent.offsetX },
                    { key: "y", type: "number", value: ev.nativeEvent.offsetY },
                    {
                      key: "clientOffset",
                      type: "json",
                      value: JSON.stringify({
                        x: ev.nativeEvent.offsetX,
                        y: ev.nativeEvent.offsetY,
                      }),
                    },
                  ],
                })
                createSnack("Custom event sent!")
              }}
            >
              Emit
            </Button>
          </Paper>
        </Grid>
      </Grid>
      <Typography variant="h6" className={classes.heading}>
        Embedded content
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <Paper className={classes.paper}>
            <Box display="flex" mb={1}>
              <Box mr={1}>
                <FormControl>
                  <InputLabel variant="outlined" htmlFor="embedded-size-select">
                    Size
                  </InputLabel>
                  <Select
                    id="embedded-size-select"
                    label="Size"
                    variant="outlined"
                    value={embeddedSize.key}
                    onChange={(ev) => {
                      const size = embeddedSizes.find(
                        (s) => s.key === ev.target.value
                      )
                      size && setEmbeddedSize(size)
                    }}
                  >
                    {embeddedSizes.map((size) => (
                      <MenuItem key={size.key} value={size.key}>
                        {size.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box mr={1}>
                <TextField
                  label="Embedded ID"
                  variant="outlined"
                  value={embeddedId}
                  onChange={(ev) => {
                    setEmbeddedId(ev.target.value)
                  }}
                />
              </Box>
            </Box>
            <EmbeddedContainer
              embeddedId={embeddedId}
              width={embeddedSize.width}
              height={embeddedSize.height}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  )

  function getTag(group: string, tag: string): Tag | null {
    return channelTags.find((t) => t.tag === tag && t.group === group) ?? null
  }

  function getAttribute(key: string): Attribute | null {
    return channelAttributes[key] ?? null
  }
}

function convertAttributeValueForInput(attr: Attribute): string {
  if (isTextAttribute(attr)) {
    return attr.value
  }
  if (isNumberAttribute(attr)) {
    return attr.value.toString(10)
  }
  if (isDateAttribute(attr)) {
    const dt = new Date(attr.value)
    const [val] = dt.toISOString().split("T")
    if (!val) {
      throw new Error(`invalid date ${attr.value}`)
    }

    return val
  }

  throw new Error("invalid attribute")
}

export default Home
